import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, FileText, Plus, Save, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { INITIAL_MOCK_ITEMS } from '@/modules/inventory/items/types'
import type { AdjustmentLine, InventoryAdjustmentRow } from '../types'
import { newAdjustmentLine, quantityAdjusted } from '../types'

const MOCK_WAREHOUSES = [
  { value: 'w1', label: 'Warehouse 1' },
  { value: 'w2', label: 'Warehouse 2' },
]
const MOCK_BATCHES = [
  { value: 'b1', label: 'Batch A' },
  { value: 'b2', label: 'Batch B' },
  { value: 'b3', label: 'Batch C' },
]
const MOCK_COST_ACCOUNTS = [
  { value: 'c1', label: '5000 — Inventory adjustment' },
  { value: 'c2', label: '5100 — Stock variance' },
]

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

export const InventoryAdjustmentEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const stateAdj = (location.state as { adjustment?: InventoryAdjustmentRow } | null)?.adjustment
  const isNew = !id || id === 'new'

  const [adjustmentDate, setAdjustmentDate] = useState(todayIso())
  const [warehouseId, setWarehouseId] = useState('')
  const [reference, setReference] = useState('')
  const [description, setDescription] = useState('')
  const [reason, setReason] = useState('')
  const [costAccountId, setCostAccountId] = useState('')

  const [lines, setLines] = useState<AdjustmentLine[]>(() =>
    Array.from({ length: 5 }, () => newAdjustmentLine())
  )

  const itemOptions = useMemo(
    () =>
      INITIAL_MOCK_ITEMS.map((i) => ({
        value: String(i.id),
        label: `${i.code} — ${i.name}`,
      })),
    []
  )

  useEffect(() => {
    if (!stateAdj || isNew) return
    setReference(stateAdj.reference)
    setWarehouseId(
      stateAdj.warehouse === 'Warehouse 1' ? 'w1' : stateAdj.warehouse === 'Warehouse 2' ? 'w2' : ''
    )
  }, [stateAdj, isNew])

  const handleBack = () => navigate('/inventory/adjustments')

  const addLines = (n: number) => {
    setLines((prev) => [...prev, ...Array.from({ length: n }, () => newAdjustmentLine())])
  }

  const updateLine = (index: number, patch: Partial<AdjustmentLine>) => {
    setLines((prev) => {
      const next = [...prev]
      const prevItemId = next[index].itemId
      const line = { ...next[index], ...patch }
      if (patch.itemId !== undefined && patch.itemId !== prevItemId) {
        line.currentQty = 10
        line.newQty = 10
      }
      next[index] = line
      return next
    })
  }

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!warehouseId) {
      toast.error(t('inventoryAdjustments.validationWarehouse'))
      return
    }
    toast.success(t('inventoryAdjustments.saveStub'))
    navigate('/inventory/adjustments')
  }

  const tableHeaderClass =
    'h-11 border-0 bg-primary/5 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary/5 dark:bg-primary/10 dark:text-primary'

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryAdjustments.formTitle')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryAdjustments.formSubtitle')}</p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={handleBack} className="w-full sm:w-auto">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('common.back')}
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('inventoryAdjustments.sectionAdjustmentDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="adj-date">{t('inventoryAdjustments.adjustmentDate')}</Label>
              <Input
                id="adj-date"
                type="date"
                value={adjustmentDate}
                onChange={(e) => setAdjustmentDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {t('inventoryAdjustments.warehouse')} <span className="text-destructive">*</span>
              </Label>
              <Select value={warehouseId || undefined} onValueChange={setWarehouseId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('inventoryAdjustments.selectWarehouse')} />
                </SelectTrigger>
                <SelectContent>
                  {MOCK_WAREHOUSES.map((w) => (
                    <SelectItem key={w.value} value={w.value}>
                      {w.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="adj-ref">{t('inventoryAdjustments.reference')}</Label>
              <Input id="adj-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
            <div className="space-y-2 md:col-span-1">
              <Label htmlFor="adj-desc">{t('inventoryAdjustments.description')}</Label>
              <Input id="adj-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="adj-reason">{t('inventoryAdjustments.reason')}</Label>
              <Input id="adj-reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('inventoryAdjustments.costAccount')}</Label>
              <Select value={costAccountId || undefined} onValueChange={setCostAccountId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('inventoryAdjustments.selectCostAccount')} />
                </SelectTrigger>
                <SelectContent>
                  {MOCK_COST_ACCOUNTS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="flex flex-col gap-4 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">{t('inventoryAdjustments.sectionAdjustedItems')}</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)}>
                <Plus className="mr-1 h-4 w-4" />
                {t('inventoryAdjustments.addRow')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)}>
                {t('inventoryAdjustments.add3Rows')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)}>
                {t('inventoryAdjustments.add5Rows')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className={tableHeaderClass}>{t('inventoryAdjustments.colItem')}</TableHead>
                    <TableHead className={tableHeaderClass}>{t('inventoryAdjustments.colBatch')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>
                      {t('inventoryAdjustments.colCurrentQty')}
                    </TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>
                      {t('inventoryAdjustments.colNewQty')}
                    </TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>
                      {t('inventoryAdjustments.colQtyAdjusted')}
                    </TableHead>
                    <TableHead className={`${tableHeaderClass} w-[64px] text-center`}>
                      {t('common.delete')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, index) => (
                    <TableRow key={line.id}>
                      <TableCell className="align-top py-2">
                        <Select
                          value={line.itemId || undefined}
                          onValueChange={(v) => updateLine(index, { itemId: v })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('inventoryAdjustments.selectItem')} />
                          </SelectTrigger>
                          <SelectContent>
                            {itemOptions.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="align-top py-2">
                        <Select
                          value={line.batchId || undefined}
                          onValueChange={(v) => updateLine(index, { batchId: v })}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('inventoryAdjustments.selectBatch')} />
                          </SelectTrigger>
                          <SelectContent>
                            {MOCK_BATCHES.map((b) => (
                              <SelectItem key={b.value} value={b.value}>
                                {b.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="py-2 text-center align-top">
                        <Input
                          readOnly
                          className="h-9 bg-muted/50 text-center"
                          value={line.itemId ? String(line.currentQty) : ''}
                        />
                      </TableCell>
                      <TableCell className="py-2 text-center align-top">
                        <Input
                          type="number"
                          className="h-9 text-center"
                          min={0}
                          value={line.newQty || ''}
                          onChange={(e) =>
                            updateLine(index, { newQty: Number(e.target.value) || 0 })
                          }
                        />
                      </TableCell>
                      <TableCell className="py-2 text-center align-top">
                        <Input
                          readOnly
                          className="h-9 bg-muted/50 text-center"
                          value={line.itemId ? String(quantityAdjusted(line)) : ''}
                        />
                      </TableCell>
                      <TableCell className="py-2 text-center align-top">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => removeLine(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit">
            <Save className="mr-2 h-4 w-4" />
            {t('inventoryAdjustments.save')}
          </Button>
        </div>
      </form>
    </div>
  )
}
